import { ChangeDetectionStrategy, Component, DestroyRef, Input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { DataNoRowOverlayComponent } from '@examdojo/ag-grid';
import { getAnimationAppearOnTrigger } from '@examdojo/animation';
import { SyllabusQuery } from '@examdojo/category/v2';
import { DateTimeFormat, DateTimeService } from '@examdojo/core/date-time';
import { MasterListViewComponent, provideSimpleListView } from '@examdojo/core/list-view';
import { ErrorHandlerService } from '@examdojo/error-handling';
import {
  getReferenceQuestionStatusLabel,
  ReferenceQuestionService,
  ReferenceQuestionStatus,
  ReferenceQuestionStatusList,
  ReferenceQuestionViewStoreModel,
} from '@examdojo/reference-question';
import { SOURCE_STATUS_LIST, SOURCE_STATUS_TO_LABEL, SourceListItemHttpModel, SourceStatus } from '@examdojo/source';
import { LoaderComponent } from '@examdojo/ui/loader';
import {
  ColDef,
  FilterModel,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  RowClickedEvent,
  SortModelItem,
  ValueFormatterParams,
} from 'ag-grid-community';
import { EMPTY, finalize, first, tap } from 'rxjs';
import { CmsUrlParts, NEW_QUESTION_ID_PARAM } from '../../cms/cms.model';
import { AgGirdColumnStateManager } from '../../shared/ag-grid/ag-gird-column-state.manager';
import { ReferenceQuestionListService } from '../services/reference-question-list.service';
import { ReferenceQuestionListSourceStatusCellRendererComponent } from './reference-question-list-source-status-cell-renderer.component';
import { ReferenceQuestionListStatusCellRendererComponent } from './reference-question-list-status-cell-renderer.component';

const REF_QUESTION_LIST_VIEW_STATE_STORAGE_KEY = 'reference-question-grid-state';

@Component({
  selector: 'exam-reference-question-list',
  templateUrl: './reference-question-list.component.html',
  styleUrl: './reference-question-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full' },
  imports: [MasterListViewComponent, LoaderComponent],
  providers: [provideSimpleListView(ReferenceQuestionListService)],
  animations: [getAnimationAppearOnTrigger()],
})
export class ReferenceQuestionListComponent {
  constructor(
    private readonly dateTimeService: DateTimeService,
    private readonly router: Router,
    private readonly service: ReferenceQuestionService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly destroyRef: DestroyRef,
    private readonly syllabusQuery: SyllabusQuery,
  ) {
    this.syllabusQuery.active$
      .pipe(
        tap(() => this.gridApi()?.refreshServerSide()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
  @Input() agGridAdditionalClass = 'ag-theme-basic-table small-height';
  readonly gridApi = signal<GridApi<ReferenceQuestionViewStoreModel> | null>(null);
  readonly isLoading = signal<boolean>(true);

  readonly columnDefs: Array<ColDef<ReferenceQuestionViewStoreModel>> = [
    {
      field: 'id',
      headerName: 'ID',
      valueGetter: ({ data }) => {
        return data?.id ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      minWidth: 150,
      cellClass: 'truncate',
      cellRenderer: ReferenceQuestionListStatusCellRendererComponent,
      filterParams: {
        values: ReferenceQuestionStatusList,
        valueFormatter: (
          params: ValueFormatterParams<ReferenceQuestionViewStoreModel, ReferenceQuestionStatus | null>,
        ) => getReferenceQuestionStatusLabel(params.value),
      },
    },
    {
      field: 'type',
      headerName: 'Type',
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
      width: 100,
    },
    {
      headerName: 'Updated At',
      field: 'updated_at',
      valueFormatter: ({ data }) => {
        if (!data || !data.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDateRelativeToNow(data.updated_at, true);
      },
      tooltipValueGetter: ({ data }) => {
        if (!data || !data.updated_at) {
          return '';
        }
        return this.dateTimeService.formatDate(data.updated_at, DateTimeFormat.ShortDateTime);
      },
    },
    {
      field: 'source_name',
      headerName: 'Source Name',
      valueGetter: ({ data }) => {
        return data?.source_name ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'source_status',
      headerName: 'Source Status',
      cellRenderer: ReferenceQuestionListSourceStatusCellRendererComponent,
      filter: 'agSetColumnFilter',
      floatingFilter: true,
      filterParams: {
        values: SOURCE_STATUS_LIST,
        valueFormatter: (params: ValueFormatterParams<SourceListItemHttpModel, SourceStatus>) =>
          SOURCE_STATUS_TO_LABEL[params.value as SourceStatus],
      },
    },
    {
      field: 'topics',
      headerName: 'Topics',
      valueGetter: ({ data }) => {
        return data?.topics?.map((topic) => topic.code).join(', ') ?? '';
      },
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'total_marks',
      headerName: 'Total Marks',
      valueGetter: ({ data }) => {
        return data?.total_marks ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'total_stems',
      headerName: 'Total Stem',
      valueGetter: ({ data }) => {
        return data?.total_stems ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
    {
      field: 'published_derivative_questions',
      headerName: 'Published Derivative Questions',
      valueGetter: ({ data }) => {
        return data?.published_derivative_questions ?? '';
      },
      filter: 'agNumberColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['equals'],
        maxNumConditions: 1,
      },
    },
  ];

  readonly serverSideDatasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => {
      const { startRow, endRow, sortModel, filterModel } = params.request;
      const sortBy: SortModelItem[] = sortModel.length ? sortModel : [{ colId: 'updated_at', sort: 'desc' }];

      this.service
        .fetchPaginatedListView({
          startRow,
          endRow: endRow ? endRow - 1 : undefined,
          sortBy: sortBy,
          filterBy: {
            ...filterModel,
            syllabus_id: {
              filterType: 'set',
              values: [this.syllabusQuery.getActiveId()!],
            },
          } as FilterModel,
        })
        .pipe(
          first(),
          tap({
            next: (data) => {
              params.success({ rowData: data.data, rowCount: data.totalCount });
              this.columnStateManager.initialRowsLoaded.set(true);
            },
            error: () => {
              params.fail();
            },
          }),
          this.errorHandlerService.catchError(
            `[QuestionListComponent.getRows]: failed to load the questions`,
            () => EMPTY,
            { toast: `Could not load reference questions` },
          ),
          finalize(() => this.isLoading.set(false)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    },
  };

  readonly gridOptions: GridOptions<ReferenceQuestionViewStoreModel> = {
    pagination: true,
    paginationAutoPageSize: true,
    cacheBlockSize: 100,
    rowModelType: 'serverSide',
    serverSideDatasource: this.serverSideDatasource,
    suppressCellFocus: true,
    allowContextMenuWithControlKey: true,
    noRowsOverlayComponent: DataNoRowOverlayComponent,
    noRowsOverlayComponentParams: {
      text: 'No reference questions found',
      body: 'Please try with another filter',
    },
    defaultColDef: {
      sortable: true,
      resizable: true,
      suppressMovable: true,
      menuTabs: [],
    },
    context: {
      componentParent: this,
    },
    animateRows: true,
    rowSelection: 'single',
    getContextMenuItems: (event) => {
      // Return only custom menu items, excluding any default AG-Grid items including delete
      return [
        {
          name: 'Open in New Tab',
          icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
          action: () => {
            if (!event.node?.data) {
              return;
            }

            const activeSyllabus = this.syllabusQuery.getActive();
            if (!activeSyllabus) {
              return;
            }

            window.open(
              `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.ReferenceQuestions}/${event.node?.data.id}`,
              '_blank',
              'noopener,noreferrer',
            );
          },
        },
        {
          name: 'Open Source in New Tab',
          icon: `<i class="fa-light fa-arrow-up-right-from-square"></i>`,
          action: () => {
            if (!event.node?.data) {
              return;
            }

            const activeSyllabus = this.syllabusQuery.getActive();
            if (!activeSyllabus) {
              return;
            }

            window.open(
              `/${CmsUrlParts.Syllabus}/${activeSyllabus.slug}/${CmsUrlParts.Sources}/${event.node?.data.source_id}`,
              '_blank',
              'noopener,noreferrer',
            );
          },
        },
      ];
    },

    rowClass: 'cursor-pointer',
    onPaginationChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onSortChanged: () => {
      this.columnStateManager.saveGridState();
    },
    onFilterChanged: () => {
      this.columnStateManager.saveGridState();
    },

    getRowId: (params) => (params.data?.id ? `${params.data.id}` : ''),
  };

  readonly columnStateManager = new AgGirdColumnStateManager(
    this.gridOptions,
    REF_QUESTION_LIST_VIEW_STATE_STORAGE_KEY,
  );

  addReferenceQuestion() {
    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate([
      '/',
      CmsUrlParts.Syllabus,
      activeSyllabus.slug,
      CmsUrlParts.ReferenceQuestions,
      NEW_QUESTION_ID_PARAM,
    ]);
  }

  openReferenceQuestion(event: RowClickedEvent<ReferenceQuestionViewStoreModel, void>) {
    if (!event.data) {
      return;
    }

    const activeSyllabus = this.syllabusQuery.getActive();
    if (!activeSyllabus) {
      return;
    }

    this.router.navigate([
      '/',
      CmsUrlParts.Syllabus,
      activeSyllabus.slug,
      CmsUrlParts.ReferenceQuestions,
      event.data.id,
    ]);
  }

  onGridReady(event: GridReadyEvent<ReferenceQuestionViewStoreModel>) {
    this.gridApi.set(event.api);

    this.columnStateManager.gridApi.set(event.api);
    this.columnStateManager.gridReady.set(true);
    this.columnStateManager.loadGridState();
  }
}
